import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { formSubmissionService } from "../services/api";
import { AuthContext } from "../context/AuthContext";

export const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filterStatus, setFilterStatus] = useState("");
  const [filterReferente, setFilterReferente] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const isAdmin = user?.role?.name === "secretaria";

  useEffect(() => {
    fetchSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      let response;
      if (isAdmin) {
        response = await formSubmissionService.getAllSubmissions(1, 100);
      } else {
        response = await formSubmissionService.getMySubmissions(1, 100);
      }
      const list = response.data.submissions || [];
      setAllSubmissions(list);
      setSubmissions(list);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros localmente
  useEffect(() => {
    let filtered = [...allSubmissions];

    if (filterStatus) {
      filtered = filtered.filter((s) =>
        filterStatus === "pendiente_link"
          ? s.workflowStatus === "pendiente_solicitante"
          : s.status === filterStatus,
      );
    }

    if (filterReferente) {
      filtered = filtered.filter(
        (s) => String(s.submittedBy?.id || s.userId) === filterReferente,
      );
    }

    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      filtered = filtered.filter((s) => new Date(s.createdAt) >= from);
    }

    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59);
      filtered = filtered.filter((s) => new Date(s.createdAt) <= to);
    }

    setSubmissions(filtered);
  }, [
    filterStatus,
    filterReferente,
    filterDateFrom,
    filterDateTo,
    allSubmissions,
  ]);

  // --- Stats ---
  const totalRegistros = allSubmissions.filter(
    (s) => s.workflowStatus !== "pendiente_solicitante",
  ).length;

  const pendientes = allSubmissions.filter(
    (s) =>
      s.status === "en_revision" &&
      s.workflowStatus !== "pendiente_solicitante",
  ).length;

  // Agrupar por referente
  const byReferente = allSubmissions
    .filter((s) => s.workflowStatus !== "pendiente_solicitante")
    .reduce((acc, s) => {
      const nombre = s.submittedBy
        ? `${s.submittedBy.firstName} ${s.submittedBy.lastName}`.trim()
        : "Sin nombre";
      acc[nombre] = (acc[nombre] || 0) + 1;
      return acc;
    }, {});
  const topReferente = Object.entries(byReferente).sort((a, b) => b[1] - a[1]);

  // Lista de referentes para el filtro
  const referentesOptions = Array.from(
    new Map(
      allSubmissions
        .filter((s) => s.submittedBy)
        .map((s) => [
          String(s.submittedBy.id),
          `${s.submittedBy.firstName} ${s.submittedBy.lastName}`,
        ]),
    ).entries(),
  );

  const handleViewDetails = (submissionId) => {
    navigate(`/submission/${submissionId}`);
  };

  const handleDeleteEmptyLink = async (submissionId) => {
    if (!window.confirm("Eliminar este link? No podra deshacerse.")) return;
    try {
      await formSubmissionService.deleteEmptyLink(submissionId);
      setAllSubmissions((prev) => prev.filter((s) => s.id !== submissionId));
    } catch (err) {
      alert(
        "Error al eliminar: " + (err.response?.data?.message || err.message),
      );
    }
  };

  const statusBadgeClass = (status, workflowStatus) => {
    if (workflowStatus === "pendiente_solicitante")
      return "badge bg-slate-200 text-slate-600";
    const map = {
      en_revision: "badge bg-amber-100 text-amber-700",
      aprobado: "badge bg-green-100 text-green-700",
      denegado: "badge bg-red-100 text-red-700",
    };
    return map[status] || "badge bg-slate-100 text-slate-600";
  };

  const statusLabel = (status, workflowStatus) => {
    if (workflowStatus === "pendiente_solicitante") return "Link pendiente";
    const map = { en_revision: "En revisión", aprobado: "Aprobado", denegado: "Denegado" };
    return map[status] || status;
  };

  const clearFilters = () => {
    setFilterStatus("");
    setFilterReferente("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  return (
    <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="mt-1 text-sm text-slate-500">Bienvenido, {user?.firstName}</p>
      </div>

      {/* Estadísticas (solo secretaria) */}
      {isAdmin && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="card p-5 border-l-4 border-primary-700">
            <p className="label">Total solicitudes</p>
            <p className="mt-1 text-4xl font-bold text-slate-800">{totalRegistros}</p>
          </div>
          <div className="card p-5 border-l-4 border-amber-400">
            <p className="label">En revisión</p>
            <p className="mt-1 text-4xl font-bold text-slate-800">{pendientes}</p>
          </div>
          <div className="card p-5 border-l-4 border-green-500 overflow-y-auto max-h-36">
            <p className="label mb-2">Por referente</p>
            {topReferente.length === 0 ? (
              <p className="text-sm text-slate-400">Sin datos</p>
            ) : (
              topReferente.map(([nombre, count]) => (
                <div key={nombre} className="flex justify-between text-sm mb-1">
                  <span className="text-slate-700 truncate">{nombre}</span>
                  <span className="font-bold text-primary-700 ml-2">{count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Filtros (solo secretaria) */}
      {isAdmin && (
        <div className="card p-4 mb-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="label">Estado</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input py-1.5 min-w-[140px]"
              >
                <option value="">Todos los estados</option>
                <option value="en_revision">En Revisión</option>
                <option value="aprobado">Aprobado</option>
                <option value="denegado">Denegado</option>
                <option value="pendiente_link">Link pendiente</option>
              </select>
            </div>
            <div>
              <label className="label">Referente</label>
              <select
                value={filterReferente}
                onChange={(e) => setFilterReferente(e.target.value)}
                className="input py-1.5 min-w-[160px]"
              >
                <option value="">Todos los referentes</option>
                {referentesOptions.map(([id, nombre]) => (
                  <option key={id} value={id}>{nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Desde</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="input py-1.5"
              />
            </div>
            <div>
              <label className="label">Hasta</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="input py-1.5"
              />
            </div>
            {(filterStatus || filterReferente || filterDateFrom || filterDateTo) && (
              <button onClick={clearFilters} className="btn-secondary py-1.5 px-3 text-sm">
                Limpiar filtros
              </button>
            )}
            <span className="ml-auto self-end text-xs text-slate-400">
              {submissions.length} resultado{submissions.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}

      {/* Tabla */}
      {loading ? (
        <div className="flex justify-center items-center py-16">
          <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        </div>
      ) : submissions.length === 0 ? (
        <div className="card p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-slate-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-slate-500">No hay solicitudes para mostrar.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">DNI</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Persona</th>
                  {isAdmin && <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Referente</th>}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Fecha</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {submissions.map((submission) => {
                  const isPendingLink = submission.workflowStatus === "pendiente_solicitante";
                  return (
                    <tr key={submission.id} className={isPendingLink ? "bg-amber-50" : "hover:bg-slate-50"}>
                      <td className="px-4 py-3 text-sm font-medium text-slate-800">
                        {submission.dniDeceased || (
                          <span className="text-slate-400 italic">Sin completar</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{submission.namingDeceased || "-"}</td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {submission.submittedBy
                            ? `${submission.submittedBy.firstName} ${submission.submittedBy.lastName}`
                            : "-"}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className={statusBadgeClass(submission.status, submission.workflowStatus)}>
                          {statusLabel(submission.status, submission.workflowStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {new Date(submission.createdAt || submission.submittedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          {!isPendingLink && (
                            <button
                              onClick={() => handleViewDetails(submission.id)}
                              className="btn-secondary py-1 px-3 text-xs"
                            >
                              Ver detalles
                            </button>
                          )}
                          {isPendingLink && (user?.role?.name === "referente" || user?.role?.name === "usuario") && (
                            <button
                              onClick={() => handleDeleteEmptyLink(submission.id)}
                              className="btn-danger py-1 px-3 text-xs"
                            >
                              Eliminar link
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </main>
  );
};
